import { ButtonType, ButtonWidget, TextAreaWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React = require('react');
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { AddKeyPairAuthCredentialRequest, IAddKeyPairAuthCredentialResult } from '~src/connectivity/serverApi/requests/credentials/AddKeyPairAuthCredentialRequest';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { textFieldWidget } from '~src/userSettings/styles/EmailAndDisplayNameFormStyles';

import { AddKeyPairAuthDialogStyles } from '../styles/AddKeyPairAuthDialogStyles';

const VALID_FILE_TYPES = ['.p8'];

// revise the regex pattern from https://www.tutorialspoint.com/How-to-validate-URL-address-in-JavaScript
const hostPattern = new RegExp('((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{1,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))');// OR ip (v4) address

export interface PrivateKeyAuthSetting {
  server: string;
  role?: string;
  username: string;
  privateKey: string;
  privateKeyPassword?: string;
}

export interface PrivateKeyAuthState {
  file?: File;
  fileName?: string;
}

export interface PrivateKeyAuthProps {
  dbClass: string;
  readonly dismiss: () => void;
  readonly close: (result: IAddKeyPairAuthCredentialResult) => void;
}

const embeddedWhiteSpace = /\s/;

function validateHost(value: string): boolean {
  return !!hostPattern.test(value);
}

export function validateServer(value: string): string {
  let v: string;
  if (!value || ((v = value.trim()) && !validateHost(v))) {
    return Messages.addNewSavedCredentialKeyPairAuthInvalidServer();
  }
  return '';
}

export function validateRole(value: string): string {
  if (value && embeddedWhiteSpace.test(value)) {
    return Messages.addNewSavedCredentialKeyPairAuthInvalidRole();
  }
  return '';
}

export function validateUsername(value: string): string {
  if (!value || embeddedWhiteSpace.test(value)) {
    return Messages.addNewSavedCredentialKeyPairAuthInvalidUsername();
  }
  return '';
}


export class AddKeyPairAuthCredentialDialog extends React.Component<PrivateKeyAuthProps, PrivateKeyAuthState> {
  props: PrivateKeyAuthProps;
  state: PrivateKeyAuthState;

  localFile: File;
  fileInput: React.RefObject<HTMLInputElement>;

  private isFileUploaded = false;

  public constructor(props: PrivateKeyAuthProps) {
    super(props);
    this.state = { file: this.localFile, fileName: '' };
    this.fileInput = React.createRef();
  }

  private handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only set the file if a user has selected a file. They may have hit cancel
    if (event.target.files.length > 0) {
      // Set the name of the file and set the current file reference.
      const fileUploaded: File = event.target.files[0];
      this.localFile = fileUploaded;
      this.setState({ file: fileUploaded, fileName: this.localFile.name });
      this.isFileUploaded = true;
    }
  };

  private triggerFileInput = () => {
    this.fileInput.current.click();
  };

  /**
   * Helper method that Promises the file content of the uploaded private key file.
   * 
   * @param keyFile private key file (pkcs#8 format) uploaded by user.
   * @returns A promise for the string content of the uploaded file
   */
  private async readPrivateKeyFileContent(keyFile: File): Promise<string> {
    let content = '';
    // let the server side check the key file
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onerror = () => {
        reader.abort();
        reject(Messages.addNewSavedCredentialKeyPairAuthInvalidFile());
      };
      reader.onloadend = (event) => {
        content = event.target.result as string;
        resolve(content);
      };
      reader.readAsText(keyFile, 'UTF-8');
    });
  }

  private getServerSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='server'>{Messages.server()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='server'
            testId='server'
            label=''
            validate={validateServer}
            className={textFieldWidget}
            showValidationMessage={true}
            placeholder='tableau.snowflakecomputing.com'
          />
        </td>
      </tr>
    );
  }

  private getRoleSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='role'>{Messages.role()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='role'
            testId='role'
            label=''
            validate={validateRole}
            className={textFieldWidget}
            showValidationMessage={true}
            placeholder='optional'
          />
        </td>
      </tr>
    );
  }

  private getUsernameSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='username'>{Messages.addNewSavedCredentialKeyPairAuthUsername()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='username'
            testId='username'
            label=''
            validate={validateUsername}
            className={textFieldWidget}
            placeholder=''
          />
        </td>
      </tr>
    );
  }

  private getPrivateKeyContentSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='privateKey'>{Messages.addNewSavedCredentialKeyPairAuthPrivateKeyFile()}</label>
        </td>
        <td className={AddKeyPairAuthDialogStyles.fileSelectionContainer}>
          <input
            data-tb-test-id="add-private-key-file"
            type="file"
            name="file"
            ref={this.fileInput}
            style={{ display: 'none' }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFileSelection(event)}
            accept={VALID_FILE_TYPES.join(',')}
          />
          <table><tbody>
            <tr>
              <td className={AddKeyPairAuthDialogStyles.fileNameField}>
                {this.state?.fileName &&
                  <TextAreaWidget
                    value={this.state.fileName}
                    label=''
                    testId="private-key-file-namae"
                    rows={1}
                    style={{ width: '170px' }}
                    maxLength={256}
                  />
                }
              </td>
              <td>
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  type="button"
                  testId="upload-private-key-file"
                  children={Messages.contentUpload_chooseAFile()}
                  handleClick={this.triggerFileInput}
                />
              </td>
            </tr>
          </tbody></table>
        </td>
      </tr>
    );
  }

  private getPrivateKeyPasswordSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='privKeyPwd'>{Messages.addNewSavedCredentialKeyPairAuthPrivateKeyFilePassword()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='privateKeyPassword'
            testId='privateKeyPassword'
            label=''
            className={textFieldWidget}
            placeholder='optional'
            type={'password'}
          />
        </td>
      </tr>
    );
  }

  async sendAddKeyPairAuthCredentialRequest(value: PrivateKeyAuthSetting, formik: FormikHelpers<any>): Promise<IAddKeyPairAuthCredentialResult> {
    if (this.state?.fileName) {
      window.localStorage.setItem([value.username, value.privateKeyPassword].join(':'), this.state.fileName);
    }

    // Await will block the rest of this code block until readPrivateKeyFileContent resolves
    const privKeyContent = await this.readPrivateKeyFileContent(this.state.file);

    let request = new AddKeyPairAuthCredentialRequest({
        server: value.server,
        role: value.role,
        username: value.username,
        privateKey: privKeyContent,
        privateKeyPassword: value.privateKeyPassword,
        dbClass: 'snowflake'
    });

    return WebHttpClient.sendRequest(request);
  }

  public render() {
    return (
      <Formik
        initialValues={{ username: '', role: '', privateKey: '', privateKeyPassword: '' }}
        onSubmit={async (value: PrivateKeyAuthSetting, formik: FormikHelpers<any>) => {
          try {
            let result: IAddKeyPairAuthCredentialResult = await this.sendAddKeyPairAuthCredentialRequest(value, formik);
            if (!result || result.errors) {
              this.props.dismiss();
              ToasterService.instance.pop({ type: ToastType.Error, text: Messages.addNewSavedCredentialKeyPairAuthFailed() });
            } else {
              this.props.close(result);
            }
          } catch (error) {
              this.props.dismiss();
              ToasterService.instance.pop({ type: ToastType.Error, text: Messages.addNewSavedCredentialKeyPairAuthFailed() });
          }
        }}
      >
        {(formik: FormikProps<PrivateKeyAuthSetting>) => (
          <form onSubmit={formik.handleSubmit} data-tb-test-id='add-keypair-auth-credential-dialog'>
            <div className='tb-dialog-title'>{Messages.addKeyPairAuthCredentialDialogTitle()}</div>
            <table className='tb-form-table'>
              <tbody>
                {this.getServerSection()}
                {this.getRoleSection()}
                {this.getUsernameSection()}
                {this.getPrivateKeyContentSection()}
                {this.getPrivateKeyPasswordSection()}
              </tbody>
            </table>
            <div className='tb-dialog-actions'>
              <ButtonWidget buttonType={ButtonType.Outline}
                testId='add-keypair-auth-dialog-dismiss'
                onClick={() => this.props.dismiss()}
                disabled={formik.isSubmitting}
                type='button'>{Messages.dismiss()}
              </ButtonWidget>
              <ButtonWidget buttonType={ButtonType.Primary}
                testId='add-keypair-auth-dialog-add-private-key-button'
                className='tb-margin-left-wide'
                onFocus={() => true}
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting || !this.isFileUploaded}
                type='submit'>{Messages.add()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
